import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { leadService } from '../services/api';
import { Spinner } from '../components/Spinner';
import { DrillDownPanel } from '../components/reports/DrillDownPanel';
import { useDrillDown } from '../hooks/useDrillDown';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { RangePicker, RangeNote, BLANK_RANGE_FILTERS, drillDates } from '../components/reports/ReportRange';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const COLS = [
  { key: 'totalLeads',  label: 'Total Enquiries', params: {} },
  { key: 'testDrive',   label: 'Test Drives',     params: { hasTestDrive: 'true' } },
  { key: 'bookings',    label: 'Bookings',        params: { convertedSubType: 'booked' } },
  { key: 'retail',      label: 'Retail Sales',    params: { convertedSubType: 'retail' } },
  { key: 'lostAll',     label: 'Lost Leads',      params: { status: 'lost' } },
  { key: 'overdue',     label: 'Overdue Leads',   params: { overdue: 'true' } },
  { key: 'noFollowups', label: 'No Follow-up',    params: { noFollowup: 'true' } },
];

// A count cell that toggles an inline drill-down row when clicked.
const DrillCell = ({ count, active, onToggle, className = '' }) => {
  if (!count) return <td className={`px-4 py-3 text-gray-700 ${className}`}>{count}</td>;
  return (
    <td className={`px-4 py-3 ${className}`}>
      <button
        onClick={onToggle}
        className={`font-medium hover:underline ${active ? 'text-primary-800 underline' : 'text-primary-600'}`}
      >
        {count}
      </button>
    </td>
  );
};

export const FunnelReport = () => {
  const [filters, setFilters] = useState(BLANK_RANGE_FILTERS);
  const [applied, setApplied] = useState(BLANK_RANGE_FILTERS);

  const { data, isLoading } = useQuery(
    ['funnelReport', applied],
    () => leadService.getFunnelReport(applied),
    { keepPreviousData: true }
  );

  // The window the API actually used — drives the note and the drill-downs.
  const appliedRange = data?.data?.data?.range;
  const rows = data?.data?.data?.rows || [];
  const teams = data?.data?.data?.teams || [];
  const others = data?.data?.data?.others || [];
  const grandTotal = data?.data?.data?.grandTotal;

  // A row's owner label: a real user, a since-deleted user, or genuinely
  // unassigned (no owner id at all).
  const ownerName = (r) => (r.user ? `${r.user.firstName} ${r.user.lastName}` : r._id ? 'Deleted user' : 'Unassigned');

  const drill = useDrillDown(leadService.getLeads);
  const { expanded, toggle: drillToggle, close, search, setSearch, leads, total, loading: drillLoading, loadingMore, hasMore, loadMore } = drill;

  const toggle = (rowKey, colKey, userId, label, userName, colParams) => {
    drillToggle(
      `${rowKey}_${colKey}`,
      `${userName} — ${label}`,
      { assignedTo: userId, ...drillDates(appliedRange), ...colParams }
    );
  };
  // Team totals drill into the dealer's whole subtree (same as the leads list's dealer filter).
  const toggleTeam = (dealerId, colKey, label, teamName, colParams) => {
    drillToggle(
      `team_${dealerId}_${colKey}`,
      `${teamName} (team) — ${label}`,
      { dealerId, ...drillDates(appliedRange), ...colParams }
    );
  };

  // One table row for a person, optionally indented under their dealer.
  const personRow = (r, { indent = false, tag = null } = {}) => {
    const rowKey = r._id || 'unassigned';
    const userName = ownerName(r);
    const rowExpanded = expanded?.key?.startsWith(`${rowKey}_`);
    return (
      <React.Fragment key={rowKey}>
        <tr className="hover:bg-gray-50">
          <td className={`px-4 py-3 text-gray-900 ${indent ? 'pl-9 text-gray-700' : 'font-medium'}`}>
            {indent && <span className="text-gray-300 mr-2">↳</span>}
            {userName}
            {tag && <span className="ml-2 text-[11px] font-semibold uppercase tracking-wide text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded">{tag}</span>}
          </td>
          {COLS.map(c => (
            <DrillCell
              key={c.key}
              count={r[c.key]}
              active={expanded?.key === `${rowKey}_${c.key}`}
              onToggle={() => r._id && toggle(rowKey, c.key, r._id, c.label, userName, c.params)}
            />
          ))}
        </tr>
        {rowExpanded && (
          <DrillDownPanel colSpan={colSpan} title={expanded.title} leads={leads} total={total} isLoading={drillLoading}
            onClose={close} search={search} onSearchChange={setSearch} hasMore={hasMore} onLoadMore={loadMore} loadingMore={loadingMore} />
        )}
      </React.Fragment>
    );
  };

  const chartData = rows.slice(0, 10).map(r => ({
    name: ownerName(r),
    'Total Enquiries': r.totalLeads,
    Bookings: r.bookings,
    'Retail Sales': r.retail,
    'Lost Leads': r.lostAll,
  }));

  const colSpan = COLS.length + 1;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ChartBarIcon className="h-6 w-6 text-primary-600" />
          Funnel Report
        </h1>
        <p className="mt-1 text-gray-600">
          Funnel by team member — total leads, test drives, bookings, retail, lost, overdue and no-follow-up counts.
          Each dealer is shown with the executives under them and a team total (the figure the dashboard's dealer filter shows).
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100 mb-4 px-5 py-4 flex flex-wrap items-center gap-2">
        <RangePicker value={filters} onChange={setFilters} />
        <button onClick={() => { setApplied(filters); close(); }} className="btn-primary text-sm">Search</button>
        <button onClick={() => { setFilters(BLANK_RANGE_FILTERS); setApplied(BLANK_RANGE_FILTERS); close(); }} className="btn-outline-secondary text-sm">Clear Filter</button>
        <div className="w-full"><RangeNote applied={appliedRange} /></div>
      </div>

      {isLoading ? (
        <div className="p-12 text-center"><Spinner size="lg" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400 text-sm">No data for this period.</div>
      ) : (
        <>
          {/* Chart */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-5 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Top team members — leads breakdown</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Total Enquiries" fill="#ef6c1a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Bookings" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Retail Sales" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Lost Leads" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Sales Executive</th>
                    {COLS.map(c => (
                      <th key={c.key} className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {teams.map(t => {
                    const teamExpanded = expanded?.key?.startsWith(`team_${t.dealerId}_`);
                    const selfRow = t.self || { _id: t.dealerId, user: { firstName: t.name, lastName: '' }, ...Object.fromEntries(COLS.map(c => [c.key, 0])) };
                    return (
                      <React.Fragment key={`team_${t.dealerId}`}>
                        {personRow(selfRow, { tag: 'Dealer' })}
                        {t.members.map(m => personRow(m, { indent: true }))}
                        {t.members.length > 0 && (
                          <>
                            <tr className="bg-primary-50/60">
                              <td className="px-4 py-2.5 text-sm font-semibold text-primary-800">
                                Team total · {t.name} <span className="font-normal text-primary-600">({t.members.length} executive{t.members.length === 1 ? '' : 's'})</span>
                              </td>
                              {COLS.map(c => (
                                <td key={c.key} className="px-4 py-2.5 font-semibold text-primary-800">
                                  {t.totals[c.key] ? (
                                    <button onClick={() => toggleTeam(t.dealerId, c.key, c.label, t.name, c.params)}
                                      className={`hover:underline ${expanded?.key === `team_${t.dealerId}_${c.key}` ? 'underline' : ''}`}>
                                      {t.totals[c.key]}
                                    </button>
                                  ) : t.totals[c.key]}
                                </td>
                              ))}
                            </tr>
                            {teamExpanded && (
                              <DrillDownPanel colSpan={colSpan} title={expanded.title} leads={leads} total={total} isLoading={drillLoading}
                                onClose={close} search={search} onSearchChange={setSearch} hasMore={hasMore} onLoadMore={loadMore} loadingMore={loadingMore} />
                            )}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {others.map(r => personRow(r))}
                  {/* older API without grouping: plain list */}
                  {!teams.length && !others.length && rows.map(r => personRow(r))}
                </tbody>
                {grandTotal && (
                  <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                    <tr>
                      <td className="px-4 py-3 font-bold text-gray-900">Grand Total</td>
                      {COLS.map(c => (
                        <td key={c.key} className="px-4 py-3 font-bold text-gray-900">{grandTotal[c.key]}</td>
                      ))}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
